"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { usePropertyById } from "@/hooks/usePropertyById";
import { PropertyDetailsScreen } from "@/components/dashboard/PropertyDetailsScreen";
import { ChevronLeft } from "lucide-react";
import Link from "next/link";
import Button from "@/components/ui/Button";
import { Loader2 } from "lucide-react";

const AUTO_CLOSE_SECONDS = 5;
const MESSAGE_NO_DATA = "This property has no data to explore.";

function PropertyNoDataOrError({
	message,
	autoCloseSeconds,
}: {
	message: string;
	autoCloseSeconds: number;
}) {
	const router = useRouter();
	const [secondsLeft, setSecondsLeft] = useState(autoCloseSeconds);

	useEffect(() => {
		if (secondsLeft <= 0) {
			router.push("/dashboard");
			return;
		}
		const t = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
		return () => clearInterval(t);
	}, [secondsLeft, router]);

	return (
		<div className="flex flex-col items-center justify-center min-h-screen p-6 text-center">
			<div className="bg-red-50 p-8 rounded-3xl border border-red-100 max-w-md">
				<h2 className="text-2xl font-bold text-red-900 mb-4">Oops!</h2>
				<p className="text-red-700 mb-4">{message}</p>
				<p className="text-sm text-red-600 mb-8">
					Closing in {secondsLeft} second
					{secondsLeft !== 1 ? "s" : ""}…
				</p>
				<Link href="/dashboard">
					<Button className="bg-[#16251c] text-white px-8">
						Back to Dashboard
					</Button>
				</Link>
			</div>
		</div>
	);
}

interface PropertyDetailClientProps {
	propertyId: string;
	state: string;
	county: string;
}

export function PropertyDetailClient({
	propertyId,
	state,
	county,
}: PropertyDetailClientProps) {
	const { data: property, isLoading, error } = usePropertyById(propertyId);

	if (isLoading) {
		return (
			<div className="min-h-screen bg-gray-50/50 flex items-center justify-center">
				<div className="text-center">
					<Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-[#16251c]" />
					<p className="text-gray-600">Loading property details...</p>
				</div>
			</div>
		);
	}

	if (error || !property) {
		const isNoData =
			error instanceof Error &&
			error.message.includes("Empty response from property API");
		const message = isNoData
			? MESSAGE_NO_DATA
			: error instanceof Error
				? error.message
				: "Property not found";
		return (
			<PropertyNoDataOrError
				message={message}
				autoCloseSeconds={AUTO_CLOSE_SECONDS}
			/>
		);
	}

	// Create JSON-LD Structured Data
	const jsonLd = {
		"@context": "https://schema.org",
		"@type": "RealEstateListing",
		name: property.address,
		description:
			property.description ||
			`Property in ${property.county}, ${property.state}`,
		url: `https://www.taxlienvest.com/${state}/${county}/property/${propertyId}`,
		address: {
			"@type": "PostalAddress",
			streetAddress: property.address,
			addressLocality: property.city || property.county,
			addressRegion: property.state,
			postalCode: property.zip,
			addressCountry: property.country || "US",
		},
		geo: {
			"@type": "GeoCoordinates",
			latitude: property.latitude,
			longitude: property.longitude,
		},
	};

	return (
		<div className="min-h-screen bg-gray-50/50">
			{/* Add JSON-LD script */}
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
			/>

			<nav className="bg-white border-b border-gray-100 px-6 py-4 sticky top-0 z-20">
				<div className="max-w-6xl mx-auto flex items-center justify-between">
					<Link
						href="/dashboard"
						className="flex items-center gap-2 text-gray-600 hover:text-[#16251c] transition-colors font-medium"
					>
						<ChevronLeft className="h-5 w-5" />
						Back to Listings
					</Link>
					<div className="hidden md:block">
						<p className="text-xs text-gray-400 uppercase tracking-widest font-bold">
							LienVest Property Intelligence
						</p>
					</div>
				</div>
			</nav>

			<div className="flex-1 pb-20">
				<PropertyDetailsScreen property={property} isLoading={false} />
			</div>
		</div>
	);
}
